import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { API_BASE } from '../../../config/api'
import { useReveal } from '../../../hooks/useReveal'

interface AboutContent {
  sectionTitle: string
  sectionSubtitle: string
  description: string
  story: { title: string; paragraphs: string[] }
  mission: { title: string; text: string }
  values: Array<{ icon: string; title: string; description: string }>
  achievements: Array<{ number: string; label: string; color?: string }>
}

const defaultAboutContent: AboutContent = {
  sectionTitle: 'Crafting Digital Excellence',
  sectionSubtitle: 'About Us',
  description: 'We are a passionate team of developers, designers, and innovators dedicated to creating stunning digital experiences that push the boundaries of what\'s possible.',
  story: {
    title: 'Our Story',
    paragraphs: [
      'Founded with a vision to bridge the gap between creativity and technology, Ionic Errrrs Code has evolved into a powerhouse of digital innovation. Our journey began with a simple belief: great software should be both functional and beautiful.',
      'Today, we specialize in mobile and web applications, bringing together the latest technologies with timeless design principles to create experiences that users love and businesses depend on.'
    ]
  },
  mission: {
    title: 'Our Mission',
    text: 'To empower businesses with innovative digital solutions that drive growth, enhance user experiences, and create lasting impact in the digital landscape.'
  },
  values: [
    { icon: '▣', title: 'Innovation', description: 'We embrace cutting-edge technologies and creative solutions to solve complex problems.' },
    { icon: '◑', title: 'Precision', description: 'Every line of code is crafted with attention to detail and performance optimization.' },
    { icon: '◦', title: 'Collaboration', description: 'We work closely with clients to understand their vision and deliver exceptional results.' },
    { icon: '▤', title: 'Excellence', description: 'Our commitment to quality drives us to exceed expectations in every project.' },
  ],
  achievements: [
    { number: '99%', label: 'Client Satisfaction' },
    { number: '48hrs', label: 'Avg Response Time' },
    { number: '500K+', label: 'Lines of Code' },
    { number: '24/7', label: 'Support Available' },
  ]
}

export default function AboutSection() {
  const reveal = useReveal<HTMLDivElement>()
  const valuesReveal = useReveal<HTMLDivElement>()
  const [aboutContent, setAboutContent] = useState<AboutContent>(defaultAboutContent)

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/v1/content/about`)
        const data = await response.json()
        if (data.success && data.data) {
          setAboutContent({
            ...defaultAboutContent,
            ...data.data,
            story: {
              ...defaultAboutContent.story,
              ...(data.data.story || {}),
            },
            mission: {
              ...defaultAboutContent.mission,
              ...(data.data.mission || {}),
            },
            values: data.data.values ?? defaultAboutContent.values,
            achievements: data.data.achievements ?? defaultAboutContent.achievements,
          })
        }
      } catch (error) {
        console.error('Failed to fetch about content:', error)
      }
    }

    fetchAbout()
  }, [])

  return (
    <section id="about" className="section-spacing">
      <div className="container-page">
        {/* Section Header */}
        <div className="section-head">
          <span className="eyebrow">{aboutContent.sectionSubtitle}</span>
          <h2 className="mt-4" style={{ color: 'var(--text)' }}>{aboutContent.sectionTitle}</h2>
          <p className="mt-4 text-[1.06rem]" style={{ color: 'var(--muted)' }}>
            {aboutContent.description}
          </p>
        </div>

        {/* Story + Mission */}
        <div
          ref={reveal.ref}
          className="grid lg:grid-cols-2 gap-16 items-start"
          style={{
            opacity: reveal.isVisible ? 1 : 0,
            transform: reveal.isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity .7s var(--ease), transform .7s var(--ease)',
          }}
        >
          <div className="space-y-8">
            <div className="space-y-5">
              <h3 className="font-display text-2xl font-semibold" style={{ color: 'var(--text)' }}>
                {aboutContent.story.title}
              </h3>
              {aboutContent.story.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 30)} className="text-[1.06rem] leading-relaxed" style={{ color: 'var(--muted)' }}>
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="card-studio">
              <h4 className="font-display text-lg font-semibold mb-3" style={{ color: 'var(--text)' }}>
                {aboutContent.mission.title}
              </h4>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
                {aboutContent.mission.text}
              </p>
            </div>
          </div>

          {/* Values Grid */}
          <div
            ref={valuesReveal.ref}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            style={{
              opacity: valuesReveal.isVisible ? 1 : 0,
              transform: valuesReveal.isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity .7s var(--ease) .15s, transform .7s var(--ease) .15s',
            }}
          >
            {aboutContent.values.map((value) => (
              <div key={value.title} className="card-studio text-center">
                <div className="icon-chip mx-auto mb-3">{value.icon}</div>
                <h4 className="font-display text-base font-semibold mb-2" style={{ color: 'var(--text)' }}>
                  {value.title}
                </h4>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Achievements Stats */}
        <div className="mt-20">
          <div
            className="stats-bar rounded-card overflow-hidden"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
            }}
          >
            {aboutContent.achievements.map((stat, i) => (
              <div
                key={stat.label}
                className="text-center py-6 px-4"
                style={{
                  borderRight: i < aboutContent.achievements.length - 1 ? '1px solid var(--border)' : 'none',
                }}
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <div className="cta-block" style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h3 className="font-display text-2xl font-semibold" style={{ color: 'var(--inverse-text)' }}>
              Ready to Start Your Project?
            </h3>
            <p className="mt-3 text-[1.06rem]" style={{ color: 'var(--inverse-text)', opacity: 0.6, maxWidth: '500px', margin: '12px auto 0' }}>
              Let's work together to bring your vision to life with our expertise and innovative approach.
            </p>
            <div className="mt-8">
              <Link to="/contact" className="btn-pill btn-inverse no-underline">
                Start Your Project
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
